import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { ApiModule, Application, EntraGroup, GrantRule, ResourceEntry, Role } from '../lib/types';
import { Badge, Card, ConfirmDelete, Empty, Field, Modal, Notice, PageHead } from '../components/ui';
import { PermissionMatrix } from '../components/PermissionMatrix';

export function RoleDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const role = useAsync(() => api.get<Role>(`/api/admin/roles/${id}`), [id]);
  const modules = useAsync(() => api.get<ApiModule[]>('/api/admin/modules'));
  const applicationId = role.data?.application_id ?? null;
  // A bound role only matters within its application's modules; offering the
  // others would let someone store rules that never take effect.
  const application = useAsync(
    () => (applicationId ? api.get<Application>(`/api/applications/${applicationId}`) : Promise.resolve(null)),
    [applicationId],
  );
  const [moduleId, setModuleId] = useState('');
  const [mapping, setMapping] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const moduleOptions = (modules.data ?? []).filter(
    (module) => !application.data || application.data.moduleAccess.some((access) => access.module_id === module.id),
  );
  const chosen = moduleId || moduleOptions[0]?.id || '';

  if (role.error) return <Notice tone="danger">{role.error}</Notice>;
  if (!role.data) return <p className="muted">Loading...</p>;

  async function unmap(mappingId: string): Promise<void> {
    await api.delete(`/api/admin/roles/${id}/groups/${mappingId}`);
    role.reload();
  }

  return (
    <>
      <PageHead
        title={role.data.name}
        description={role.data.description ?? undefined}
        actions={
          <>
            {role.data.application_id ? (
              <Badge tone="accent">
                Role of <Link to={`/applications/${role.data.application_id}`}>{role.data.application_name}</Link>
              </Badge>
            ) : (
              <Badge>Global role</Badge>
            )}
            <button className="danger" onClick={() => setDeleting(true)}>
              Delete role
            </button>
          </>
        }
      />

      {role.data.application_id ? (
        <Notice tone="info">
          This role counts only for people acting through {role.data.application_name}. The same person coming
          through another application does not carry it.
        </Notice>
      ) : null}

      <Card
        title="Entra groups"
        hint="members of these groups receive the role at their next sign-in"
        actions={
          <button className="primary small" onClick={() => setMapping(true)}>
            Map group
          </button>
        }
        tight
      >
        {role.data.groups.length ? (
          <table>
            <tbody>
              {role.data.groups.map((group) => (
                <tr key={group.id}>
                  <td>{group.group_name ?? <span className="muted">without a name</span>}</td>
                  <td className="mono muted">{group.entra_group_id}</td>
                  <td>
                    <button className="small danger" onClick={() => void unmap(group.id)}>
                      Unmap
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No group mapped - this role is only assigned by hand.</Empty>
        )}
      </Card>

      <RoleGrants roleId={id} moduleId={chosen} modules={moduleOptions} onModuleChange={setModuleId} />

      {deleting ? (
        <ConfirmDelete
          title="Delete role"
          entity={<>Delete the role <strong>{role.data.name}</strong>?</>}
          impact={[
            role.data.memberCount
              ? `${role.data.memberCount} ${role.data.memberCount === 1 ? 'person loses' : 'people lose'} the permissions of this role.`
              : 'Nobody currently holds this role.',
            role.data.groups.length
              ? `${role.data.groups.length} Entra group ${role.data.groups.length === 1 ? 'mapping is' : 'mappings are'} removed - the groups themselves stay untouched.`
              : 'No Entra group is mapped to it.',
            'All permissions granted through this role are dropped.',
          ]}
          confirmText={role.data.memberCount ? role.data.name : undefined}
          confirmLabel="Delete role"
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await api.delete(`/api/admin/roles/${id}`);
            navigate('/roles');
          }}
        />
      ) : null}

      {mapping ? (
        <MapGroupDialog
          roleId={id}
          onClose={() => setMapping(false)}
          onSaved={() => {
            setMapping(false);
            role.reload();
          }}
        />
      ) : null}
    </>
  );
}

function RoleGrants({
  roleId,
  moduleId,
  modules,
  onModuleChange,
}: {
  roleId: string;
  moduleId: string;
  modules: ApiModule[];
  onModuleChange: (id: string) => void;
}) {
  const resources = useAsync(
    () => (moduleId ? api.get<ResourceEntry[]>(`/api/admin/modules/${moduleId}/resources`) : Promise.resolve([])),
    [moduleId],
  );
  const stored = useAsync(
    () =>
      moduleId ? api.get<GrantRule[]>(`/api/admin/roles/${roleId}/modules/${moduleId}/grants`) : Promise.resolve([]),
    [moduleId, roleId],
  );
  const [draft, setDraft] = useState<GrantRule[] | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const rules = draft ?? stored.data ?? [];

  async function save(): Promise<void> {
    await api.put(`/api/admin/roles/${roleId}/modules/${moduleId}/grants`, {
      rules: rules.map((rule) => ({ resource: rule.resource, action: rule.action, effect: rule.effect })),
    });
    setDraft(null);
    stored.reload();
    setMessage('Saved.');
  }

  return (
    <>
      {message ? <Notice tone="ok">{message}</Notice> : null}
      <Card
        title="Permissions of this role"
        actions={
          <div className="row">
            <select
              value={moduleId}
              onChange={(event) => {
                onModuleChange(event.target.value);
                setDraft(null);
              }}
            >
              {modules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </select>
            <button className="primary small" onClick={() => void save()} disabled={draft === null}>
              Save
            </button>
          </div>
        }
        tight
      >
        <PermissionMatrix resources={resources.data ?? []} rules={rules} onChange={setDraft} />
      </Card>
    </>
  );
}

function MapGroupDialog({
  roleId,
  onClose,
  onSaved,
}: {
  roleId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<EntraGroup | null>(null);
  const [byHand, setByHand] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [groupName, setGroupName] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The list comes out of the cache, so typing may search on every keystroke -
  // a short pause is only there to keep the request count sane.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const groups = useAsync(
    () => api.get<EntraGroup[]>(`/api/admin/entra-groups?q=${encodeURIComponent(query)}`),
    [query],
  );

  const chosenId = byHand ? groupId.trim() : (picked?.entra_group_id ?? '');
  const chosenName = byHand ? groupName.trim() : (picked?.display_name ?? '');
  const empty = !groups.loading && !groups.error && !groups.data?.length;

  async function refresh(): Promise<void> {
    setRefreshing(true);
    setError(null);
    try {
      await api.post('/api/admin/entra-groups/sync');
      groups.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setRefreshing(false);
    }
  }

  async function save(): Promise<void> {
    try {
      await api.post(`/api/admin/roles/${roleId}/groups`, {
        entraGroupId: chosenId,
        groupName: chosenName || undefined,
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      title="Map an Entra group"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void save()} disabled={!chosenId}>
            Map
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {groups.error ? <Notice tone="danger">{groups.error}</Notice> : null}

      {byHand ? (
        <>
          <Field label="Object id of the group" hint="from the Entra portal">
            <input
              type="text"
              value={groupId}
              onChange={(event) => setGroupId(event.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </Field>
          <Field label="Display name" hint="optional, only for recognition">
            <input
              type="text"
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder="SG-Support-L1"
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Group" hint="search by name or object id">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="SG-Support"
              autoFocus
            />
          </Field>

          {picked ? (
            <p className="muted tiny">
              Selected: <strong>{picked.display_name ?? 'without a name'}</strong>{' '}
              <span className="mono">{picked.entra_group_id}</span>
            </p>
          ) : null}

          <div className="picker-list">
            {empty ? (
              <Empty>
                {query
                  ? 'No group matches that.'
                  : 'No groups cached yet - fetch them from Entra once.'}
              </Empty>
            ) : (
              (groups.data ?? []).map((group) => (
                <button
                  key={group.entra_group_id}
                  type="button"
                  className={`picker-option${picked?.entra_group_id === group.entra_group_id ? ' selected' : ''}`}
                  onClick={() => setPicked(group)}
                >
                  <span>{group.display_name ?? <span className="muted">without a name</span>}</span>
                  <span className="mono muted tiny">{group.entra_group_id}</span>
                </button>
              ))
            )}
          </div>

          <div className="row">
            <button className="small" onClick={() => void refresh()} disabled={refreshing}>
              {refreshing ? 'Fetching...' : 'Refresh from Entra'}
            </button>
          </div>
        </>
      )}

      <p className="muted tiny">
        <button className="link" type="button" onClick={() => setByHand((value) => !value)}>
          {byHand ? 'Pick from the list instead' : 'Enter an object id by hand'}
        </button>
      </p>

      <p className="muted tiny">
        Membership is derived from the group again on every sign-in and on every group sync. If somebody
        leaves the group, they lose the role - roles assigned manually stay untouched by that.
      </p>
      <Badge tone="accent">Applied at the next group sync</Badge>
    </Modal>
  );
}
